import React, { useState, useEffect } from 'react';
import { Users, CheckCircle, Trash2, Activity, Clock, Plus, Key } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'analyst' });
  const [loading, setLoading] = useState(false);

  const generate = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let pwd = '';
    for (let i = 0; i < 12; i++) pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    setForm({ ...form, password: pwd });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await axios.post(`${API}/api/users/create`, form);
      alert('User created! Temp password: ' + res.data.temporary_password);
      onCreated();
      onClose();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: '16px', width: '600px', padding: '32px' }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '24px' }}>Create User</h2>
        <form onSubmit={submit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '6px' }}>Email</label>
            <input type='email' value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required style={{ width: '100%', padding: '10px', border: '1px solid var(--border-light)', borderRadius: '8px' }} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '6px' }}>Name</label>
            <input type='text' value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required style={{ width: '100%', padding: '10px', border: '1px solid var(--border-light)', borderRadius: '8px' }} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '6px' }}>Temp Password</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input type='text' value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required style={{ flex: 1, padding: '10px', border: '1px solid var(--border-light)', borderRadius: '8px' }} />
              <button type='button' onClick={generate} className='btn btn-secondary'><Key size={16} /> Gen</button>
            </div>
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '6px' }}>Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-light)', borderRadius: '8px' }}>
              <option value='admin'>Admin</option>
              <option value='analyst'>Analyst</option>
              <option value='viewer'>Viewer</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type='submit' className='btn btn-primary' disabled={loading} style={{ flex: 1, justifyContent: 'center' }}>{loading ? 'Creating...' : 'Create'}</button>
            <button type='button' onClick={onClose} className='btn btn-secondary' style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [stats, setStats] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [u, p, s] = await Promise.all([
        axios.get(`${API}/api/users`),
        axios.get(`${API}/api/users/pending-approval`),
        axios.get(`${API}/api/users/stats`)
      ]);
      setUsers(u.data.users || []);
      setPending(p.data.pending_users || []);
      setStats(s.data);
    } catch (err) {
      console.error(err);
    }
  };

  const approve = async (id) => {
    await axios.put(`${API}/api/users/${id}/approve`);
    alert('Approved!');
    load();
  };

  const changeRole = async (id, role) => {
    await axios.put(`${API}/api/users/${id}`, { role });
    load();
  };

  const del = async (id) => {
    if (!confirm('Delete user?')) return;
    await axios.delete(`${API}/api/users/${id}`);
    load();
  };

  return (
    <div className='card'>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h3 className='card-title'>User Management</h3>
          <p className='card-subtitle'>Manage access and roles</p>
        </div>
        <button onClick={() => setShowCreate(true)} className='btn btn-primary'><Plus size={20} /> Create User</button>
      </div>
      
      {stats && (
        <div className='grid grid-cols-4' style={{ gap: '12px', marginBottom: '24px' }}>
          <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '10px', textAlign: 'center' }}>
            <Users size={24} style={{ margin: '0 auto 8px', color: 'var(--primary-500)' }} />
            <p style={{ fontSize: '0.75rem' }}>Total</p>
            <p style={{ fontSize: '1.75rem', fontWeight: '700' }}>{stats.total_users}</p>
          </div>
          <div style={{ padding: '16px', background: 'hsl(38, 92%, 95%)', borderRadius: '10px', textAlign: 'center' }}>
            <Clock size={24} style={{ margin: '0 auto 8px', color: 'var(--warning-600)' }} />
            <p style={{ fontSize: '0.75rem' }}>Pending</p>
            <p style={{ fontSize: '1.75rem', fontWeight: '700' }}>{stats.pending_approval}</p>
          </div>
          <div style={{ padding: '16px', background: 'hsl(142, 76%, 96%)', borderRadius: '10px', textAlign: 'center' }}>
            <CheckCircle size={24} style={{ margin: '0 auto 8px', color: 'var(--success-600)' }} />
            <p style={{ fontSize: '0.75rem' }}>Approved</p>
            <p style={{ fontSize: '1.75rem', fontWeight: '700' }}>{stats.approved}</p>
          </div>
          <div style={{ padding: '16px', background: 'hsl(199, 89%, 96%)', borderRadius: '10px', textAlign: 'center' }}>
            <Activity size={24} style={{ margin: '0 auto 8px', color: 'var(--info-600)' }} />
            <p style={{ fontSize: '0.75rem' }}>Sessions</p>
            <p style={{ fontSize: '1.75rem', fontWeight: '700' }}>{stats.active_sessions}</p>
          </div>
        </div>
      )}

      {pending.length > 0 && (
        <div style={{ marginBottom: '24px', padding: '16px', background: 'hsl(38, 92%, 95%)', borderRadius: '12px', border: '2px solid var(--warning-500)' }}>
          <h4 style={{ fontWeight: '700', marginBottom: '12px' }}>⚠ {pending.length} Pending</h4>
          {pending.map((u, i) => (
            <div key={i} style={{ padding: '12px', background: 'white', borderRadius: '8px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                {u.picture && <img src={u.picture} alt={u.name} style={{ width: '40px', height: '40px', borderRadius: '8px' }} />}
                <div>
                  <p style={{ fontWeight: '700' }}>{u.name}</p>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{u.email}</p>
                </div>
              </div>
              <button onClick={() => approve(u.user_id)} className='btn' style={{ background: 'var(--success-500)', color: 'white', border: 'none' }}><CheckCircle size={16} /> Approve</button>
            </div>
          ))}
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--border-light)', background: 'var(--bg-secondary)' }}>
            <th style={{ padding: '12px', textAlign: 'left' }}>User</th>
            <th style={{ padding: '12px', textAlign: 'left' }}>Email</th>
            <th style={{ padding: '12px', textAlign: 'left' }}>Role</th>
            <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
            <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--border-light)' }}>
              <td style={{ padding: '12px' }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  {u.picture && <img src={u.picture} alt={u.name} style={{ width: '40px', height: '40px', borderRadius: '8px' }} />}
                  <div>
                    <p style={{ fontWeight: '700' }}>{u.name || 'Unknown'}</p>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{u.user_id}</p>
                  </div>
                </div>
              </td>
              <td style={{ padding: '12px' }}>{u.email}</td>
              <td style={{ padding: '12px' }}>
                <select value={u.role} onChange={(e) => changeRole(u.user_id, e.target.value)} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border-light)' }}>
                  <option value='admin'>Admin</option>
                  <option value='analyst'>Analyst</option>
                  <option value='viewer'>Viewer</option>
                </select>
              </td>
              <td style={{ padding: '12px' }}>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <span className={`badge badge-${u.is_approved ? 'success' : 'warning'}`}>{u.is_approved ? '✓ Approved' : '⏳ Pending'}</span>
                  {u.auto_admin && <span className='badge' style={{ background: 'var(--accent-500)', color: 'white' }}>Auto</span>}
                </div>
              </td>
              <td style={{ padding: '12px' }}>
                <button onClick={() => del(u.user_id)} className='btn btn-secondary' style={{ padding: '6px 12px' }}><Trash2 size={14} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={load} />}
    </div>
  );
}

export default UserManagement;
